import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DisplayFieldMapping } from '../components/Form/Builder/DisplayFields';
import { EditFormMapping } from '../components/Form/Builder/EditForms';
import BackButton from '../components/UI/BackButton';
import Button from '../components/UI/Button';
import ErrorMessages from '../components/UI/ErrorMessages';
import Loader from '../components/UI/Loader';
import Modal from '../components/UI/Modal';
import './Form.css';
import { Mapping, PUBLIC_INTAKE } from '../constants/formTypes';
import { createFormField, deletedField, getClientForm, getForm, publishFormVersion, reorderField, updateField } from '../services/formsService';
import * as uuid from 'uuid';
import LoadingPlaceholder from '../components/UI/LoadingPlaceholder';
import FieldModel from '../models/pageModels/FieldModel';
import { mapFormFieldToFieldModel } from '../mappers/formFieldMapper';
import ApiErrorMessages from '../components/UI/ApiErrorMessages';
import getclassNames from '../utils/getClassNames';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { ACTIVITY_DATE_ID, BREAK_LINE_ID, CHECKBOX_GROUP_ID, CHECKBOX_ID, DATE_ID, HEADER_ID, MULTI_LINE_TEXT_ID, NOTES_ID, NUMBER_ID, PARAGRAPH_ID, RADIO_BUTTON_ID, REPEATING_STRUCTURED_ID, SELECT_LIST_ID, TEXT_ID, TIME_SPENT_ID } from '../constants/fieldTypes';
import { NEW_BREAKLINE, NEW_CHECKBOX, NEW_CHECKBOX_GROUP, NEW_DATE, NEW_HEADING, NEW_MULTI_TEXT, NEW_NOTES, NEW_NUMBER, NEW_PARAGRAPH, NEW_RADIO, NEW_SELECT, NEW_TEXT, NEW_TIME_SPENT } from '../constants/draggableIds';
import IconButton from '../components/UI/IconButton';
import Form from '../models/serviceModels/Form';
import Gender from '../models/serviceModels/Gender';
import Ethnicity from '../models/serviceModels/Ethnicity';
import Race from '../models/serviceModels/Race';
import { getEthnicities, getGenders, getRaces, getRelationships } from '../services/clientsService';
import Relationship from '../models/serviceModels/Relationship';
import CheckboxGroup from '../components/UI/CheckboxGroup';
import states from '../constants/states';
import Checkbox from '../components/UI/Checkbox';
import { OrganizationContext } from '../App';
import { optionalDateOfBirthEnabled } from '../utils/features';
import {
    getEthnicityFieldVisible,
    getGenderOptionsForOrganization,
    getRaceFieldLabel,
    getRaceOptionsForOrganization,
    isDemographicVisible,
} from '../utils/demographicsSchema';

const queryAttr = "data-rbd-drag-handle-draggable-id";
const STANDARD_PROFILE_DEMOGRAPHIC_FIELD_NAMES = [
    'main_profile_preferred_pronouns',
    'main_profile_gender',
    'main_profile_ethnicity',
    'main_profile_race'
];

const FormV2 = () => {

    const navigate = useNavigate();
    const { versionId = '', id = '' } = useParams();
    const [form, setForm] = useState<Form>();
    const [fields, setFields] = useState<FieldModel[]>([]);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [editingField, setEditingField] = useState<FieldModel>();
    const [deletingField, setDeletingField] = useState<FieldModel>();
    const [apiError, setApiError] = useState<any>();
    const [modalError, setModalError] = useState('');
    const [reordering, setReordering] = useState(false);
    const location = useLocation();
    const [controlsHidden, setControlsHidden] = useState<string[]>([]);
    const [showClientFormFieldOptions, setShowClientFormFieldOptions] = useState(true);
    const [clientForm, setClientForm] = useState<Form>();
    const [loadingClientForm, setLoadingClientForm] = useState(false);
    const [loadingreferenceData, setLoadingReferenceData] = useState(false);
    const [genders, setGenders] = useState<Gender[]>();
    const [ethnicities, setEthnicities] = useState<Ethnicity[]>();
    const [races, setRaces] = useState<Race[]>();
    const [relationships, setRelationshipts] = useState<Relationship[]>();
    const [showAddAllAdditionalInfoFields, setShowAddAllAdditionalInfoFields] = useState(false);
    const [addingAllAdditionalInfoFields, setAddingAllAdditionalInfoFields] = useState(false);
    const [showAddAllProfileFields, setShowAddAllProfileFields] = useState(false);
    const [addingAllProfileFields, setAddingAllProfileFields] = useState(false);
    const [selectedProfileFields, setSelectedProfileFields] = useState<{ label: string, value: string }[]>([]);
    const [selectedAdditionalInfoFields, setSelectedAdditionalInfoFields] = useState<{ label: string, value: string }[]>([]);
    const [recentlyAddedField, setRecentlyAddedField] = useState<string[]>([]);
    const [confirmAlignFieldsToProfile, setConfirmAlignFieldsToProfile] = useState(false);
    const [aligningToFieldsToProfile, setAligningFieldsToProfile] = useState(false);
    const [waitingToAlignToProfile, setWaitingToAlignToProfile] = useState(false);
    const [alignToProfile, setAlignToProfile] = useState(false);
    const organization = useContext(OrganizationContext);

    useEffect(() => {
        document.title = "Build or Edit a Form"
    }, []);

    useEffect(() => {
        if (!form || form.formTypeId !== PUBLIC_INTAKE) {
            return;
        }

        setLoadingReferenceData(true);

        Promise.all([getGenders(), getEthnicities(), getRaces(), getRelationships()]).then(([gr, er, rr, rel]) => {
            setGenders(gr);
            setEthnicities(er);
            setRaces(rr);
            setRelationshipts(rel);
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setLoadingReferenceData(false);
        });
    }, [form]);

    useEffect(() => {
        getForm(id, versionId).then(form => {
            setForm(form);
            setFields([
                ...form.fields.map(f => mapFormFieldToFieldModel(f))
            ]);
        });
    }, [id]);

    useEffect(() => {
        if (!waitingToAlignToProfile) {
            return;
        }

        const alignFields = async () => {
            await handleAlignFieldsToProfile();
            setWaitingToAlignToProfile(false);
            setShowAddAllProfileFields(false);
            setShowAddAllAdditionalInfoFields(false);
        };

        alignFields();
    }, [waitingToAlignToProfile]);

    useEffect(() => {
        if (form && form.formTypeId === PUBLIC_INTAKE) {
            setLoadingClientForm(true);
            getClientForm().then(f => {
                setClientForm(f);
            }).catch(e => {
                console.error(e);
                setApiError(e);
            }).finally(() => {
                setLoadingClientForm(false);
            });
        }
    }, [form]);


    const getMainProfileFieldOptions = useCallback((filterOutFixedFields = true) => {
        const demographicsConfig = organization.value;
        const showPronouns = isDemographicVisible(demographicsConfig, 'pronouns');
        const showGender = isDemographicVisible(demographicsConfig, 'gender');
        const showEthnicity = getEthnicityFieldVisible(demographicsConfig);
        const showRace = isDemographicVisible(demographicsConfig, 'race');
        const showAddress = isDemographicVisible(demographicsConfig, 'address');
        const showCity = isDemographicVisible(demographicsConfig, 'city');
        const showState = isDemographicVisible(demographicsConfig, 'state');
        const showZip = isDemographicVisible(demographicsConfig, 'zip');
        const showPhone = isDemographicVisible(demographicsConfig, 'phone');
        const showEmail = isDemographicVisible(demographicsConfig, 'email');
        const genderOptions = getGenderOptionsForOrganization(genders || [], demographicsConfig).map((g, i) => ({ label: g.label, value: g.value, order: i }));
        const stateOptions = states.map((s, i) => ({ ...s, order: i })) || [];
        const ethnicityOptions = ethnicities?.map((e, i) => ({ label: e.name, value: e.id.toString(), order: i })) || [];
        const raceOptions = getRaceOptionsForOrganization(races || [], ethnicities || [], demographicsConfig).map((r, i) => ({ label: r.label, value: r.value, order: i }));
        const relationshipOptions = relationships?.map((r, i) => ({ label: r.name, value: r.id.toString(), order: i })) || [];
        const raceFieldLabel = getRaceFieldLabel(demographicsConfig);

        const householdMembersSchema = [
            { fieldTypeId: TEXT_ID, fieldLabel: 'First Name', fieldName: 'first_name' },
            { fieldTypeId: TEXT_ID, fieldLabel: 'Middle Name', fieldName: 'middle_name' },
            { fieldTypeId: TEXT_ID, fieldLabel: 'Last Name', fieldName: 'last_name' },
            { fieldTypeId: DATE_ID, fieldLabel: 'Date of Birth', fieldName: 'date_of_birth' },
            ...(showPhone ? [{ fieldTypeId: TEXT_ID, fieldLabel: 'Phone Number', fieldName: 'phone_number', mask: "(000)000-0000" }] : []),
            ...(showEmail ? [{ fieldTypeId: TEXT_ID, fieldLabel: 'Email', fieldName: 'email' }] : []),
            { fieldTypeId: SELECT_LIST_ID, fieldLabel: 'Relationship', fieldName: 'relationship', options: relationshipOptions },
            ...(showGender ? [{ fieldTypeId: SELECT_LIST_ID, fieldLabel: 'Gender', fieldName: 'gender', options: genderOptions }] : []),
            ...(showEthnicity ? [{ fieldTypeId: RADIO_BUTTON_ID, fieldLabel: 'Ethnicity', fieldName: 'ethnicity', options: ethnicityOptions }] : []),
            ...(showRace ? [{ fieldTypeId: CHECKBOX_GROUP_ID, fieldLabel: raceFieldLabel, fieldName: 'race', options: raceOptions }] : [])
        ];

        const options = [];

        if (!filterOutFixedFields) {
            options.push({ name: 'main_profile_first_name', label: 'First Name', fieldTypeId: TEXT_ID });
        }

        options.push({ name: 'main_profile_middle_name', label: 'Middle Name', fieldTypeId: TEXT_ID });

        if (!filterOutFixedFields) {
            options.push({ name: 'main_profile_last_name', label: 'Last Name', fieldTypeId: TEXT_ID });
        }

        if (!filterOutFixedFields || optionalDateOfBirthEnabled) {
            options.push({ name: 'main_profile_date_of_birth', label: 'Date of Birth', fieldTypeId: DATE_ID });
        }

        if (showPronouns) {
            options.push({ name: 'main_profile_preferred_pronouns', label: 'Preferred Pronouns', fieldTypeId: TEXT_ID });
        }

        if (showGender) {
            options.push({ name: 'main_profile_gender', label: 'Gender', fieldTypeId: SELECT_LIST_ID, options: genderOptions });
        }

        if (showEthnicity) {
            options.push({ name: 'main_profile_ethnicity', label: 'Ethnicity', fieldTypeId: RADIO_BUTTON_ID, options: ethnicityOptions });
        }

        if (showRace) {
            options.push({ name: 'main_profile_race', label: raceFieldLabel, fieldTypeId: CHECKBOX_GROUP_ID, options: raceOptions });
        }
        if (showPhone) options.push({ name: 'main_profile_phone_number', label: 'Phone Number', fieldTypeId: TEXT_ID, mask: "(000)000-0000" });
        if (showEmail) options.push({ name: 'main_profile_email', label: 'Email', fieldTypeId: TEXT_ID });
        if (showAddress) {
            options.push({ name: 'main_profile_address_line_1', label: 'Address Line 1', fieldTypeId: TEXT_ID });
            options.push({ name: 'main_profile_address_line_2', label: 'Address Line 2', fieldTypeId: TEXT_ID });
        }
        if (showCity) options.push({ name: 'main_profile_city', label: 'City', fieldTypeId: TEXT_ID });
        if (showState) options.push({ name: 'main_profile_state', label: 'State', fieldTypeId: SELECT_LIST_ID, options: stateOptions });
        if (showZip) options.push({ name: 'main_profile_zip_code', label: 'Zip Code', fieldTypeId: TEXT_ID });
        options.push({ name: 'main_profile_family_members', label: 'Household and Family Members', fieldTypeId: REPEATING_STRUCTURED_ID, schema: householdMembersSchema });

        return options;
    }, [genders, ethnicities, races, relationships, organization.value]);

    const getAdditionalInformationFieldOptions = useCallback(() => {
        const options = [];

        if (clientForm) {
            for (const field of clientForm.fields) {
                let label = field.label;
                if (field.fieldTypeId === BREAK_LINE_ID) {
                    label = 'New Line';
                }

                options.push({
                    name: `additional_info_${field.name}`,
                    label: label,
                    fieldTypeId: field.fieldTypeId,
                    options: field.options?.map(o => ({ label: o.label, value: o.value, order: o.order }))
                });
            }
        }

        return options;
    }, [clientForm]);

    const getActiveProfileFieldDefinition = useCallback((fieldName?: string) => {
        if (!fieldName) {
            return undefined;
        }

        return getMainProfileFieldOptions(false).find(profileField => profileField.name === fieldName);
    }, [getMainProfileFieldOptions]);

    const isInactiveStandardProfileDemographicField = useCallback((fieldName?: string) => {
        return !!fieldName
            && STANDARD_PROFILE_DEMOGRAPHIC_FIELD_NAMES.includes(fieldName)
            && !getActiveProfileFieldDefinition(fieldName);
    }, [getActiveProfileFieldDefinition]);

    const getActiveProfileFieldModel = useCallback((field: FieldModel): FieldModel => {
        const activeDefinition = getActiveProfileFieldDefinition(field.name);
        if (!activeDefinition) {
            return field;
        }

        const fieldOptions = (activeDefinition.options || []).map((option, index) => {
            const existingOption = field.options?.find(existing => existing.value === option.value);
            return {
                id: existingOption?.id,
                label: option.label,
                value: option.value,
                deleted: false,
                order: option.order ?? index
            };
        });

        return {
            ...field,
            label: activeDefinition.label,
            fieldTypeId: activeDefinition.fieldTypeId,
            options: fieldOptions.length > 0 ? fieldOptions : field.options,
            schema: activeDefinition.schema ? JSON.stringify(activeDefinition.schema) : (field as any).schema,
            mask: activeDefinition.mask ?? (field as any).mask
        } as FieldModel;
    }, [getActiveProfileFieldDefinition]);

    useEffect(() => {
        if (!form || form.formTypeId !== PUBLIC_INTAKE || loadingreferenceData) {
            return;
        }

        setFields(currentFields => {
            const normalizedFields = currentFields.map(field => getActiveProfileFieldModel(field));
            return JSON.stringify(normalizedFields) === JSON.stringify(currentFields)
                ? currentFields
                : normalizedFields;
        });
    }, [form, getActiveProfileFieldModel, loadingreferenceData]);

    const syncActiveProfileFields = async () => {
        if (!form || form.formTypeId !== PUBLIC_INTAKE || !form.draft) {
            return fields;
        }

        const syncedFields: FieldModel[] = [];

        for (const field of fields) {
            if (isInactiveStandardProfileDemographicField(field.name)) {
                await deletedField({
                    id: field.id,
                    formId: id,
                    versionId
                });
                continue;
            }

            const activeField = getActiveProfileFieldModel(field);
            const fieldChanged = JSON.stringify(activeField) !== JSON.stringify(field);
            if (!fieldChanged) {
                syncedFields.push(field);
                continue;
            }

            const updatedField = await updateField({
                fieldTypeId: activeField.fieldTypeId,
                label: activeField.label,
                order: activeField.order,
                options: activeField.options,
                name: activeField.name || '',
                id: activeField.id,
                versionId,
                formId: id,
                required: activeField.required,
                sortValues: activeField.sortValues || false,
                schema: (activeField as any).schema,
                mask: (activeField as any).mask
            });

            syncedFields.push(mapFormFieldToFieldModel(updatedField));
        }

        setFields([...syncedFields]);
        return syncedFields;
    };

    const handleOnSave = (field: FieldModel) => {
        if (!form) {
            return;

        }

        if (!form.draft) {
            console.warn("Not allowed to make changes to non-draft forms");
            return;
        }

        if (!field.id) {
            return;
        }

        if (!field.name) {
            setModalError('Field Name (under Advanced) is required');
            return;
        }

        if (fields.find(f => f.name === field.name && f.id !== field.id)) {
            setModalError('A field with the same name already exists');
            return;
        }

        setSaving(true);

        updateField({
            fieldTypeId: field.fieldTypeId,
            label: field.label,
            order: field.order,
            options: field.options,
            name: field.name,
            id: field.id,
            versionId,
            formId: id,
            required: field.required,
            sortValues: field.sortValues || false,
            schema: (field as any).schema,
            mask: (field as any).mask
        }).then(uf => {
            setEditingField(undefined);

            const matchingExistingField = fields.find(f => f.id === uf.id);
            if (!matchingExistingField) {
                console.warn('No matching form field found to update');
                return;
            }

            const indexOfField = fields.indexOf(matchingExistingField);
            fields[indexOfField] = mapFormFieldToFieldModel(uf);

            setFields([
                ...fields
            ]);
        }).catch(e => {
            console.error(e);
            setModalError('Failed to update field');
        }).finally(() => {
            setSaving(false);
        });
    }

    const handlePublish = async () => {
        if (!form) {
            console.warn("No form available to publish");
            return;
        }

        if (!form.draft) {
            console.warn("Not allowed to publish non-draft forms");
            return;
        }

        setPublishing(true);

        try {
            await syncActiveProfileFields();
            await publishFormVersion(id, versionId);

            if (location.key === 'default') {
                navigate('/');
            } else {
                navigate('/admin/forms', {
                    state: {
                        publishedForm: {
                            id,
                            name: form.name
                        }
                    }
                });
            }
        } catch (e) {
            console.error(e);
            setApiError(e);
        } finally {
            setPublishing(false);
        }
    };

    const handleOnDelete = () => {
        setModalError('');

        if (!form) {
            return;
        }

        if (!form.draft) {
            console.warn("Not allowed to make changes to non-draft forms");
            return;
        }

        if (!deletingField?.id) {
            console.warn('No field selected for delete.  Unable to trigger delete');
            return;
        }

        setDeleting(true);
        deletedField({
            id: deletingField.id,
            formId: id,
            versionId
        }).then(() => {
            setFields([
                ...fields.filter(f => f.id !== deletingField.id)
            ]);
            setDeletingField(undefined);
        }).catch(e => {
            console.error(e);
            setModalError('Failed to delete field');
        }).finally(() => {
            setDeleting(false);
        });
    };

    const handleOnEdit = (field: FieldModel) => {
        setModalError('');
        setEditingField(field);
    };

    const handleSetOrder = (fieldId: string, order: number) => {
        if (!form) {
            return;
        }

        if (!form.draft) {
            console.warn("Not allowed to make changes to non-draft forms");
            return;
        }

        const existingField = fields.find(ef => ef.id === fieldId);
        if (!existingField) {
            return;
        }

        setReordering(true);
        const newFields = [];
        for (let i = 0; i < fields.length; i++) {
            const field = fields[i];
            if ((order - 1) === i) {
                if (order < existingField.order) {
                    newFields.push(existingField);
                }

                newFields.push(field);

                if (order > existingField.order) {
                    newFields.push(existingField);
                }

            } else if (fieldId !== field.id) {
                newFields.push(field);
            }
        }

        for (let i = 0; i < newFields.length; i++) {
            newFields[i].order = i + 1;
        }

        setFields([...newFields]);

        reorderField({
            formId: id,
            versionId,
            fields: [
                { id: fieldId, order }
            ]
        }).then(updatedFields => {
            setFields([
                ...updatedFields.map(uf => mapFormFieldToFieldModel(uf))
            ]);
        }).catch(e => {
            console.error(e);
            setApiError(e);
        }).finally(() => {
            setReordering(false);
        })
    };

    const newFieldsDisabled = false;

    if (!form) {
        return (<div className="section">
            <Loader />
        </div>);
    }

    const addField = (fieldTypeId: number, order: number, fieldLabel?: string, fieldName?: string, preventEditing = false, schema: any = undefined, options?: { value: string; label: string, order: number }[], mask?: string) => {
        setApiError(undefined);

        if (!form) {
            return;

        }

        if (!form.draft) {
            console.warn("Not allowed to make changes to non-draft forms");
            return;
        }

        setModalError('');
        const trackingId = uuid.v4();

        // order isn't 0 indexed
        fields.splice(order - 1, 0, {
            id: '',
            label: fieldLabel || 'Unnamed Field',
            name: '',
            order: 0,
            fieldTypeId,
            options: options?.map(o => ({ label: o.label, value: o.value, deleted: false, order: o.order })) || [],
            required: false,
            adding: true,
            addingId: trackingId
        });

        setFields([...fields]);
        createFormField({
            formId: id,
            versionId: versionId,
            fieldTypeId,
            label: fieldLabel || 'Unnamed Field',
            name: fieldName,
            order,
            options: options,
            schema: schema && JSON.stringify(schema),
            mask
        }).then(field => {
            setFields(f => {
                f = f.filter(f => f.addingId !== trackingId);
                f.splice(order - 1, 0, mapFormFieldToFieldModel(field));

                return f;
            });

            // go right into editing
            if (EditFormMapping[field.fieldTypeId] && !preventEditing) {
                setEditingField(mapFormFieldToFieldModel(field));
            }
        }).catch(e => {
            console.error(e);
            setApiError(e);
        });
    };


    const getField = (field: FieldModel) => {
        const MappedField = DisplayFieldMapping[field.fieldTypeId];
        if (!MappedField) {
            return null;
        }

        if (form.formTypeId === PUBLIC_INTAKE && field.name && (field.name.startsWith('main_profile_') || field.name.startsWith('additional_info_'))) {
            const protectDateOfBirth = !optionalDateOfBirthEnabled && field.name === 'main_profile_date_of_birth';
            return <MappedField
                hideButtons={field.required || protectDateOfBirth}
                disabled={!form.draft}
                field={protectDateOfBirth ? { ...field, required: true } : field}
                sortValues={field.sortValues}
                showAdvanced={false}
                onDelete={setDeletingField}
                preventEdit={true}
                onEdit={() => { }} />;
        }
        return <MappedField
            hideButtons={false}
            disabled={!form.draft}
            field={field}
            sortValues={field.sortValues}
            showAdvanced={false}
            preventEdit={false}
            onDelete={setDeletingField}
            onEdit={handleOnEdit} />;
    };

    const getEditForm = (field: FieldModel) => {
        const MappedForm = EditFormMapping[field.fieldTypeId];

        return (MappedForm &&
            <MappedForm
                field={field}
                onCancel={() => setEditingField(undefined)}
                onSave={handleOnSave}
                saving={saving} />);
    };

    const handleOnDragEnd = (result: any) => {
        const { source, destination, draggableId } = result;
        // make sure there is a destination (picked up and dropped outside range)
        // They droppining into the same location in the fields
        // Tried to drop into controls area (no sorry)

        if (!destination ||
            (source.droppableId === '/fields' && destination.droppableId === '/fields' && destination.index === source.index) ||
            destination.droppableId === '/controls') {
            return;
        }

        if (destination.droppableId === '/fields') {
            if (source.droppableId === '/controls') {
                let fieldId = null;
                let fieldName = undefined;
                let fieldLabel = undefined;
                let preventEditing = false;
                let schema = undefined;
                let options = undefined;
                let mask = undefined;

                switch (draggableId) {
                    case NEW_TEXT:
                        fieldId = TEXT_ID;
                        break;
                    case NEW_HEADING:
                        fieldId = HEADER_ID;
                        break;
                    case NEW_CHECKBOX:
                        fieldId = CHECKBOX_ID;
                        break;
                    case NEW_CHECKBOX_GROUP:
                        fieldId = CHECKBOX_GROUP_ID;
                        break;
                    case NEW_SELECT:
                        fieldId = SELECT_LIST_ID;
                        break;
                    case NEW_MULTI_TEXT:
                        fieldId = MULTI_LINE_TEXT_ID;
                        break;
                    case NEW_NOTES:
                        fieldId = NOTES_ID;
                        break;
                    case NEW_BREAKLINE:
                        fieldId = BREAK_LINE_ID;
                        break;
                    case NEW_RADIO:
                        fieldId = RADIO_BUTTON_ID;
                        break;
                    case NEW_TIME_SPENT:
                        fieldId = TIME_SPENT_ID;
                        break;
                    case NEW_NUMBER:
                        fieldId = NUMBER_ID;
                        break;
                    case NEW_DATE:
                        fieldId = DATE_ID;
                        break;
                    case NEW_PARAGRAPH:
                        fieldId = PARAGRAPH_ID;
                        break;
                    default:
                        if (draggableId.startsWith("/controls/main-profile/")) {
                            const profileName = draggableId.replace('/controls/main-profile/', '');
                            const profileFields = getMainProfileFieldOptions();
                            const profileField = profileFields.find(pf => pf.name === profileName);

                            fieldId = profileField?.fieldTypeId;
                            fieldLabel = profileField?.label;
                            fieldName = profileField?.name;
                            schema = profileField?.schema;
                            mask = profileField?.mask;
                            options = profileField?.options;

                            preventEditing = true;
                        } else if (draggableId.startsWith("/controls/additional-info/")) {
                            const additionalInfoName = draggableId.replace('/controls/additional-info/', '');
                            const additionalInfoFields = getAdditionalInformationFieldOptions();
                            const additionalInfoField = additionalInfoFields.find(af => af.name === additionalInfoName);

                            fieldId = additionalInfoField?.fieldTypeId;
                            fieldLabel = additionalInfoField?.label;
                            fieldName = additionalInfoField?.name;
                            options = additionalInfoField?.options;

                            preventEditing = true;
                        }
                        break;
                }

                if (!fieldId) {
                    console.warn('Unable to add field. Field Type is unknown', draggableId);
                    return;
                }

                addField(fieldId, destination.index + 1, fieldLabel, fieldName, preventEditing, schema, options, mask);
                // they are wanting to add a new field at index destination.index
            } else if (source.droppableId === '/fields') {
                if (source.index > (fields.length - 1)) {
                    console.warn(`Unable to move field at index ${source.index}. Not enough fields found`);
                    return;
                }

                const sourceField = fields[source.index];

                handleSetOrder(sourceField.id, destination.index + 1);
            }
        }
    };

    const handleHideShowControls = (type: 'Styling' | 'Categorical' | 'Special' | 'Textual' | 'MainProfile' | 'AdditionalInfo') => {
        if (controlsHidden.includes(type)) {
            setControlsHidden(controlsHidden.filter(ch => ch !== type));
        } else {
            setControlsHidden([
                ...controlsHidden,
                type
            ]);
        }
    };

    const handleOnAddAllProfileFields = () => {
        setModalError('');
        setAddingAllProfileFields(false);
        setShowAddAllProfileFields(true);
        setAlignToProfile(true);

        // preselect all fields not already added
        setSelectedProfileFields([
            ...mainProfileFields.filter(f => !fields.some(fi => fi.name === f.name)).map(f => ({ label: f.label, value: f.name }))
        ]);
    };

    const handleOnAddAllAdditionalInfoFields = () => {
        setModalError('');
        setAddingAllAdditionalInfoFields(false);
        setShowAddAllAdditionalInfoFields(true);
        setAlignToProfile(true);

        // preselect all fields not already added
        setSelectedAdditionalInfoFields([
            ...additionalInfoFields.filter(f => !fields.some(fi => fi.name === f.name)).map(f => ({ label: f.label, value: f.name }))
        ]);
    };

    const isTemplateField = (sourceField: FieldModel) => {
        const mainProfileFields = getMainProfileFieldOptions(false);
        const additionalInfoFields = getAdditionalInformationFieldOptions();
        const isTemplate = mainProfileFields.some(f => f.name === sourceField.name) || additionalInfoFields.some(f => f.name === sourceField.name);

        return isTemplate;
    };

    const executeAddAllProfileFields = async () => {
        if (selectedProfileFields.length === 0) {
            setModalError('You must select profile fields in order to add them');
            return;
        }

        setModalError('');
        setAddingAllProfileFields(true);

        let maxOrder = 1;
        for (const f of fields) {
            if (f.order > maxOrder) {
                maxOrder = f.order;
            }
        }

        if (maxOrder !== 1) {
            maxOrder++;
        }

        const possibleProfileFields = getMainProfileFieldOptions(false).filter(pf => !fields.some(f => f.name === pf.name)).map((pf, i) => {
            return {
                order: maxOrder++,
                field: pf
            }
        });

        try {
            for (const selectedField of selectedProfileFields) {
                if (fields.some(f => f.name === selectedField.value)) {
                    console.warn('Field already added, skipping', selectedField);
                    continue;
                }

                const matchingField = possibleProfileFields.find(f => f.field.name === selectedField.value);
                if (!matchingField) {
                    console.warn('Field name not found, unable to add', selectedField);
                    continue;
                }

                const createdField = await createFormField({
                    formId: id,
                    versionId,
                    fieldTypeId: matchingField.field.fieldTypeId,
                    label: matchingField.field.label || 'Unnamed Field',
                    name: matchingField.field.name,
                    order: matchingField.order,
                    options: matchingField.field.options,
                    schema: matchingField.field.schema && JSON.stringify(matchingField.field.schema),
                    mask: matchingField.field.mask
                });

                setRecentlyAddedField(raf => {
                    return [
                        ...raf,
                        createdField.id
                    ]
                });

                setFields(f => {
                    f.splice(matchingField.order - 1, 0, mapFormFieldToFieldModel(createdField));

                    return [
                        ...f
                    ];
                });

                setTimeout(() => {
                    setRecentlyAddedField(raf => {
                        return [
                            ...raf.filter(f => f !== createdField.id)
                        ]
                    });
                }, 7000);
            }

            if (alignToProfile) {
                setWaitingToAlignToProfile(true);
            } else {
                setShowAddAllProfileFields(false);
            }
        } catch (e) {
            console.error(e);
            setApiError(e);
        } finally {
            setAddingAllProfileFields(false);
        }
    }


    const executeAddAllAdditionalInfoFields = async () => {
        if (selectedAdditionalInfoFields.length === 0) {
            setModalError('You must select additional info fields in order to add them');
            return;
        }

        setModalError('');
        setAddingAllAdditionalInfoFields(true);

        let maxOrder = 1;
        for (const f of fields) {
            if (f.order > maxOrder) {
                maxOrder = f.order;
            }
        }

        if (maxOrder !== 1) {
            maxOrder++;
        }

        const possibleAdditionalInfoFields = getAdditionalInformationFieldOptions().filter(pf => !fields.some(f => f.name === pf.name)).map((pf, i) => {
            return {
                order: maxOrder++,
                field: pf
            }
        });
        try {
            for (const selectedField of selectedAdditionalInfoFields) {
                if (fields.some(f => f.name === selectedField.value)) {
                    console.warn('Field already added, skipping', selectedField);
                    continue;
                }

                const matchingField = possibleAdditionalInfoFields.find(f => f.field.name === selectedField.value);
                if (!matchingField) {
                    console.warn('Field name not found, unable to add', selectedField);
                    continue;
                }

                const createdField = await createFormField({
                    formId: id,
                    versionId,
                    fieldTypeId: matchingField.field.fieldTypeId,
                    label: matchingField.field.label || 'Unnamed Field',
                    name: matchingField.field.name,
                    order: matchingField.order,
                    options: matchingField.field.options
                });

                setRecentlyAddedField(raf => {
                    return [
                        ...raf,
                        createdField.id
                    ]
                });

                setFields(f => {
                    f.splice(matchingField.order - 1, 0, mapFormFieldToFieldModel(createdField));

                    return [
                        ...f
                    ];
                });

                setTimeout(() => {
                    setRecentlyAddedField(raf => {
                        return [
                            ...raf.filter(f => f !== createdField.id)
                        ]
                    });
                }, 7000);
            }

            if (alignToProfile) {
                setWaitingToAlignToProfile(true);
            } else {
                setShowAddAllAdditionalInfoFields(false);
            }
        } catch (e) {
            console.error(e);
            setApiError(e);
        } finally {
            setAddingAllAdditionalInfoFields(false);
        }
    }

    const getPlaceHolderHeight = (fieldTypeId: number) => {
        switch (fieldTypeId) {
            case NOTES_ID:
                return 's-4';
            case MULTI_LINE_TEXT_ID:
            case PARAGRAPH_ID:
                return 's-3';
            case TEXT_ID:
            case DATE_ID:
            case NUMBER_ID:
            case HEADER_ID:
            case ACTIVITY_DATE_ID:
            case TIME_SPENT_ID:
            case SELECT_LIST_ID:
            case CHECKBOX_ID:
                return 's-2';
            case RADIO_BUTTON_ID:
            case CHECKBOX_GROUP_ID:
                return 's-1';
            default:
                return '';
        }
    };

    const handleConfirmAlignFieldsToProfile = () => {
        setConfirmAlignFieldsToProfile(true);
        setAligningFieldsToProfile(false);
        setModalError('');
        setApiError(undefined);
    };

    const handleCancelAlignFieldsToProfile = () => {
        setConfirmAlignFieldsToProfile(false);
        setAligningFieldsToProfile(false);
        setModalError('');
        setApiError(undefined);
    };

    const handleAlignFieldsToProfile = async () => {
        setAligningFieldsToProfile(true);
        setModalError('');
        setApiError(undefined);

        const existingFieldNames = fields.map(f => f.name);

        const possibleMainProfileFields = getMainProfileFieldOptions(false).filter(pf => existingFieldNames.includes(pf.name));
        const possibleAdditionalInfoFields = getAdditionalInformationFieldOptions().filter(af => existingFieldNames.includes(af.name));

        const profileFieldQueue = [...possibleMainProfileFields];
        const additionalInfoFieldQueue = [...possibleAdditionalInfoFields];

        const newFieldOrder = [];
        for (const f of fields) {
            if (possibleMainProfileFields.some(mf => mf.name == f.name) || possibleAdditionalInfoFields.some(af => af.name === f.name)) {
                const profilePoppedFields = profileFieldQueue.splice(0, 1);
                if (profilePoppedFields.length === 1) {
                    const matchingField = fields.find(f => f.name === profilePoppedFields[0].name);
                    if (matchingField) {
                        newFieldOrder.push(matchingField);
                    } else {
                        console.error('No matching field was found for the rearrange madness', profilePoppedFields[0].name);
                    }
                } else {
                    const additionalInfoPoppedFields = additionalInfoFieldQueue.splice(0, 1);
                    if (additionalInfoPoppedFields.length === 1) {
                        const matchingField = fields.find(f => f.name === additionalInfoPoppedFields[0].name);
                        if (matchingField) {
                            newFieldOrder.push(matchingField);
                        } else {
                            console.error('No matching field was found for the rearrange madness', additionalInfoPoppedFields[0].name);
                        }
                    }
                }
            } else {
                newFieldOrder.push(f);
            }
        }

        console.log('newFieldOrder', newFieldOrder);

        // const newerFieldOrder: FieldModel[] = [];

        // for (const nf of newFieldOrder) {
        //     if (possibleAdditionalInfoFields.some(af => af.name === nf.name)) {
        //         const poppedFields = additionalInfoFieldQueue.splice(0, 1);
        //         if (poppedFields.length === 1) {
        //             const matchingField = fields.find(f => f.name === poppedFields[0].name);
        //             if (matchingField) {
        //                 newerFieldOrder.push(matchingField);
        //             } else {
        //                 console.error('No matching field was found for the rearrange madness', poppedFields[0].name);
        //             }
        //         }
        //     } else {
        //         newerFieldOrder.push(nf);
        //     }
        // }

        for (let i = 0; i < newFieldOrder.length; i++) {
            newFieldOrder[i].order = i + 1;
        }

        try {
            const reorderedFields = await reorderField({
                fields: newFieldOrder.map(f => ({ id: f.id, order: f.order })),
                formId: id,
                versionId
            });

            setFields([
                ...reorderedFields.map(rf => mapFormFieldToFieldModel(rf))
            ]);

            handleCancelAlignFieldsToProfile();
        } catch (e) {
            console.error(e);
            setApiError(e);
        } finally {
            setAligningFieldsToProfile(false);
        }
    };

    const handleHideAddAllProfileFields = () => {
        setShowAddAllProfileFields(false);
        setSelectedProfileFields([]);
        setModalError('');
        setApiError(undefined);
    }

    const handleHideAddAllAdditionalInfoFields = () => {
        setShowAddAllAdditionalInfoFields(false);
        setSelectedAdditionalInfoFields([]);
        setModalError('');
        setApiError(undefined);
    }

    const mainProfileFields = form.formTypeId === PUBLIC_INTAKE ? getMainProfileFieldOptions() : [];
    const additionalInfoFields = form.formTypeId === PUBLIC_INTAKE ? getAdditionalInformationFieldOptions() : [];

    return (<section id="main-content" tabIndex={-1}>
        <div className="section">
            <h1>Form Builder</h1>
            <div className="row margin-vertical-5 justify-content-between">
                <div className="column-medium-3">
                    <BackButton />
                </div>
                <div className="column-auto">
                    <div className="row align-items-center">
                        <div className="column-auto">
                            {reordering && <Loader noMarginTop={true} />}
                        </div>
                        {
                            form.draft && <>
                                {form.formTypeId === PUBLIC_INTAKE && <div className="column-auto">
                                    <Button id="align-fields-to-profile-button"
                                        className="small secondary"
                                        onClick={handleConfirmAlignFieldsToProfile}
                                        text="Align Fields to Profile"
                                        disabled={publishing || reordering} />
                                </div>}
                                <div className="column-auto">
                                    <Button id="publish-button"
                                        className="small"
                                        onClick={handlePublish}
                                        text="Publish"
                                        loading={publishing}
                                        disabled={publishing || reordering} />
                                </div>
                            </>
                        }
                    </div>
                </div>
            </div>
            <ApiErrorMessages error={apiError} />
            {!form.draft && <div className="row margin-vertical-5">
                <div className="column-auto">
                    Modifications to this form are disabled while it is in View mode. To make changes, switch to Edit mode to create a new draft that can be edited.
                </div>
            </div>}
            <DragDropContext onDragEnd={handleOnDragEnd}>
                <div className="row">
                    <div className="column-medium-4 sticky top-100">
                        <div className="row justify-content-center">
                            <div className="column-auto">
                                <h2>Field Options</h2>
                            </div>
                        </div>
                        <div className="fields-scroll-container">
                            <Droppable droppableId="/controls" isDropDisabled={!form.draft}>
                                {(droppableProvided) =>
                                    <div ref={droppableProvided.innerRef} className="controls-container">
                                        <h3><IconButton id={`hide-show-controls-styling`}
                                            onClick={() => handleHideShowControls('Styling')}
                                            className="small flat margin-end-5"
                                            icon={controlsHidden.includes('Styling') ? <i className="ri-arrow-down-s-line"></i> : <i className="ri-arrow-up-s-line"></i>} />Styling</h3>
                                        {!controlsHidden.includes('Styling') && <>
                                            <div className="row">
                                                <div className="column-large-6">
                                                    <Draggable draggableId={NEW_HEADING} index={0} isDragDisabled={!form.draft}>
                                                        {(providedDraggable) =>
                                                        (<div className="control-item"
                                                            ref={providedDraggable.innerRef}
                                                            {...providedDraggable.draggableProps}
                                                            {...providedDraggable.dragHandleProps}>
                                                            <i className="ri-heading margin-start-1"></i>Heading
                                                        </div>)}
                                                    </Draggable>
                                                </div>
                                                <div className="column-large-6">
                                                    <Draggable draggableId={NEW_BREAKLINE} index={1} isDragDisabled={!form.draft}>
                                                        {(providedDraggable) =>
                                                        (<div className="control-item"
                                                            ref={providedDraggable.innerRef}
                                                            {...providedDraggable.draggableProps}
                                                            {...providedDraggable.dragHandleProps}>
                                                            <i className="ri-separator margin-start-1"></i>New Line
                                                        </div>
                                                        )}
                                                    </Draggable>
                                                </div>
                                                <div className="column-large-6">
                                                    <Draggable draggableId={NEW_PARAGRAPH} index={2} isDragDisabled={!form.draft}>
                                                        {(providedDraggable) =>
                                                        (<div className="control-item"
                                                            ref={providedDraggable.innerRef}
                                                            {...providedDraggable.draggableProps}
                                                            {...providedDraggable.dragHandleProps}>
                                                            <i className="ri-align-left margin-start-1"></i>Paragraph
                                                        </div>
                                                        )}
                                                    </Draggable>
                                                </div>
                                            </div>
                                        </>}
                                        {form.formTypeId === PUBLIC_INTAKE &&
                                            <>
                                                <h3><IconButton id={`hide-show-controls-special`}
                                                    onClick={() => handleHideShowControls('MainProfile')}
                                                    className="small flat margin-end-5"
                                                    icon={controlsHidden.includes('MainProfile') ? <i className="ri-arrow-down-s-line"></i> : <i className="ri-arrow-up-s-line"></i>} />Profile</h3>
                                                {!controlsHidden.includes('MainProfile') &&
                                                    <>
                                                        <div className="row margin-bottom-3">
                                                            <div className="column text-sub">
                                                                Only one of each profile fields are allowed to be added per form
                                                            </div>
                                                        </div>
                                                        {form.draft && <div className="row margin-bottom-3">
                                                            <div className="column">
                                                                <Button id="add-all-fields-button"
                                                                    text="Add All Profile Fields"
                                                                    className="small"
                                                                    disabled={mainProfileFields.every(f => fields.some(fi => fi.name === f.name))}
                                                                    onClick={handleOnAddAllProfileFields} />
                                                            </div>
                                                        </div>}
                                                        <div className="row">
                                                            {mainProfileFields.map((f, i) => {
                                                                return <div className="column-large-6" key={f.name}>
                                                                    <Draggable draggableId={`/controls/main-profile/${f.name}`} index={4 + i} isDragDisabled={fields.some(fi => fi.name === f.name) || !form.draft}>
                                                                        {(providedDraggable) =>
                                                                        (<div className={getclassNames('control-item', fields.some(fi => fi.name === f.name) ? 'disabled' : '')}
                                                                            ref={providedDraggable.innerRef}
                                                                            {...providedDraggable.draggableProps}
                                                                            {...providedDraggable.dragHandleProps}>{f.label}
                                                                        </div>)}
                                                                    </Draggable>
                                                                </div>
                                                            })}
                                                        </div>
                                                    </>}
                                                <h3><IconButton id={`hide-show-controls-special`}
                                                    onClick={() => handleHideShowControls('AdditionalInfo')}
                                                    className="small flat margin-end-5"
                                                    icon={controlsHidden.includes('AdditionalInfo') ? <i className="ri-arrow-down-s-line"></i> : <i className="ri-arrow-up-s-line"></i>} />Additional Information</h3>
                                                {!controlsHidden.includes('AdditionalInfo') &&
                                                    <>
                                                        <div className="row margin-bottom-3">
                                                            <div className="column text-sub">
                                                                Only one of each additional information fields are allowed to be added per form
                                                            </div>
                                                        </div>
                                                        {form.draft && <div className="row margin-bottom-3">
                                                            <div className="column">
                                                                <Button id="add-all-fields-button"
                                                                    text="Add All Additional Info Fields"
                                                                    disabled={additionalInfoFields.every(f => fields.some(fi => fi.name === f.name))}
                                                                    className="small"
                                                                    onClick={handleOnAddAllAdditionalInfoFields} />
                                                            </div>
                                                        </div>}
                                                        <div className="row">
                                                            {additionalInfoFields.map((f, i) => {
                                                                return <div className="column-large-6" key={f.name}>
                                                                    <Draggable draggableId={`/controls/additional-info/${f.name}`} index={mainProfileFields.length + 4 + i} isDragDisabled={fields.some(fi => fi.name === f.name) || !form.draft}>
                                                                        {(providedDraggable) =>
                                                                        (<div className={getclassNames('control-item', fields.some(fi => fi.name === f.name) ? 'disabled' : '')}
                                                                            ref={providedDraggable.innerRef}
                                                                            {...providedDraggable.draggableProps}
                                                                            {...providedDraggable.dragHandleProps}>{f.label}
                                                                        </div>)}
                                                                    </Draggable>
                                                                </div>
                                                            })}
                                                        </div>
                                                    </>}
                                            </>}
                                        <h3><IconButton id={`hide-show-controls-textual`}
                                            onClick={() => handleHideShowControls('Textual')}
                                            className="small flat margin-end-5"
                                            icon={controlsHidden.includes('Textual') ? <i className="ri-arrow-down-s-line"></i> : <i className="ri-arrow-up-s-line"></i>} />Textual</h3>
                                        {!controlsHidden.includes('Textual') && <>
                                            <div className="row">
                                                <div className="column-large-6">
                                                    <Draggable draggableId={NEW_TEXT} index={mainProfileFields.length + additionalInfoFields.length + 5} isDragDisabled={!form.draft}>
                                                        {(providedDraggable) =>
                                                        (<div className="control-item"
                                                            ref={providedDraggable.innerRef}
                                                            {...providedDraggable.draggableProps}
                                                            {...providedDraggable.dragHandleProps}>
                                                            <i className="ri-text-snippet margin-start-1"></i>Text
                                                        </div>)}
                                                    </Draggable>
                                                </div>
                                                <div className="column-large-6">

                                                    <Draggable draggableId={NEW_MULTI_TEXT} index={mainProfileFields.length + additionalInfoFields.length + 6} isDragDisabled={!form.draft}>
                                                        {(providedDraggable) =>
                                                        (<div className="control-item"
                                                            ref={providedDraggable.innerRef}
                                                            {...providedDraggable.draggableProps}
                                                            {...providedDraggable.dragHandleProps}>
                                                            <i className="ri-text-block margin-start-1"></i>Mult-line Text

                                                        </div>)}
                                                    </Draggable>
                                                </div>
                                                <div className="column-large-6">
                                                    <Draggable draggableId={NEW_NUMBER} index={mainProfileFields.length + additionalInfoFields.length + 7} isDragDisabled={!form.draft}>
                                                        {(providedDraggable) =>
                                                        (<div className="control-item"
                                                            ref={providedDraggable.innerRef}
                                                            {...providedDraggable.draggableProps}
                                                            {...providedDraggable.dragHandleProps}>
                                                            <span className="margin-start-1">12345</span>Number

                                                        </div>)}
                                                    </Draggable>
                                                </div>
                                            </div>
                                        </>}
                                        <h3><IconButton id={`hide-show-controls-categorical`}
                                            onClick={() => handleHideShowControls('Categorical')}
                                            className="small flat margin-end-5"
                                            icon={controlsHidden.includes('Categorical') ? <i className="ri-arrow-down-s-line"></i> : <i className="ri-arrow-up-s-line"></i>} />Categorical</h3>
                                        {!controlsHidden.includes('Categorical') &&
                                            <>
                                                <div className="row">
                                                    <div className="column-large-6">
                                                        <Draggable draggableId={NEW_RADIO} index={mainProfileFields.length + additionalInfoFields.length + 8} isDragDisabled={!form.draft}>
                                                            {(providedDraggable) =>
                                                            (<div className="control-item"
                                                                ref={providedDraggable.innerRef}
                                                                {...providedDraggable.draggableProps}
                                                                {...providedDraggable.dragHandleProps}>
                                                                <i className="ri-radio-button-line margin-start-1"></i>Radio Button
                                                            </div>)}
                                                        </Draggable>
                                                    </div>
                                                    <div className="column-large-6">
                                                        <Draggable draggableId={NEW_SELECT} index={mainProfileFields.length + additionalInfoFields.length + 9} isDragDisabled={!form.draft}>
                                                            {(providedDraggable) =>
                                                            (<div className="control-item"
                                                                ref={providedDraggable.innerRef}
                                                                {...providedDraggable.draggableProps}
                                                                {...providedDraggable.dragHandleProps}><i className="ri-dropdown-list margin-start-1"></i>Select List

                                                            </div>)}
                                                        </Draggable>
                                                    </div>
                                                    <div className="column-large-6">
                                                        <Draggable draggableId={NEW_CHECKBOX} index={mainProfileFields.length + additionalInfoFields.length + 10} isDragDisabled={!form.draft}>
                                                            {(providedDraggable) =>
                                                            (<div className="control-item"
                                                                ref={providedDraggable.innerRef}
                                                                {...providedDraggable.draggableProps}
                                                                {...providedDraggable.dragHandleProps}><i className="ri-checkbox-line margin-start-1"></i>Checkbox

                                                            </div>)}
                                                        </Draggable>
                                                    </div>
                                                    <div className="column-large-6">
                                                        <Draggable draggableId={NEW_CHECKBOX_GROUP} index={mainProfileFields.length + additionalInfoFields.length + 11} isDragDisabled={!form.draft}>
                                                            {(providedDraggable) =>
                                                            (<div className="control-item"
                                                                ref={providedDraggable.innerRef}
                                                                {...providedDraggable.draggableProps}
                                                                {...providedDraggable.dragHandleProps}><i className="ri-checkbox-multiple-line margin-start-1"></i>Checkbox Group

                                                            </div>)}
                                                        </Draggable>
                                                    </div>
                                                    <div className="column-large-6">
                                                        <Draggable draggableId={NEW_DATE} index={mainProfileFields.length + additionalInfoFields.length + 12} isDragDisabled={!form.draft}>
                                                            {(providedDraggable) =>
                                                            (<div className="control-item"
                                                                ref={providedDraggable.innerRef}
                                                                {...providedDraggable.draggableProps}
                                                                {...providedDraggable.dragHandleProps}><i className="ri-calendar-line margin-start-1"></i>Date

                                                            </div>)}
                                                        </Draggable>
                                                    </div>
                                                </div>
                                            </>}
                                        <h3><IconButton id={`hide-show-controls-special`}
                                            onClick={() => handleHideShowControls('Special')}
                                            className="small flat margin-end-5"
                                            icon={controlsHidden.includes('Special') ? <i className="ri-arrow-down-s-line"></i> : <i className="ri-arrow-up-s-line"></i>} />Special</h3>
                                        {!controlsHidden.includes('Special') &&
                                            <>
                                                <div className="row margin-bottom-3">
                                                    <div className="column text-sub">
                                                        Only one Time Spent and Notes field are allowed to be added per form
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="column-large-6">
                                                        <Draggable draggableId={NEW_NOTES} index={mainProfileFields.length + additionalInfoFields.length + 13} isDragDisabled={fields.some(f => f.fieldTypeId === NOTES_ID) || !form.draft}>
                                                            {(providedDraggable) =>
                                                            (<div className={getclassNames('control-item', fields.some(f => f.fieldTypeId === NOTES_ID) ? 'disabled' : '')}
                                                                ref={providedDraggable.innerRef}
                                                                {...providedDraggable.draggableProps}
                                                                {...providedDraggable.dragHandleProps}><i className="ri-sticky-note-line margin-start-1"></i>Notes

                                                            </div>)}
                                                        </Draggable>
                                                    </div>
                                                    <div className="column-large-6">
                                                        <Draggable draggableId={NEW_TIME_SPENT} index={mainProfileFields.length + additionalInfoFields.length + 14} isDragDisabled={fields.some(f => f.fieldTypeId === TIME_SPENT_ID) || !form.draft}>
                                                            {(providedDraggable) =>
                                                            (<div className={getclassNames('control-item', fields.some(f => f.fieldTypeId === TIME_SPENT_ID) ? 'disabled' : '')}
                                                                ref={providedDraggable.innerRef}
                                                                {...providedDraggable.draggableProps}
                                                                {...providedDraggable.dragHandleProps}>
                                                                <i className="ri-time-line margin-start-1"></i>
                                                                Time Spent
                                                            </div>)}
                                                        </Draggable>
                                                    </div>
                                                </div>
                                            </>}
                                        {droppableProvided.placeholder}
                                    </div>
                                }
                            </Droppable>
                        </div>
                    </div>
                    <div className="column-medium">
                        <div className="row justify-content-center">
                            <div className="column-auto">
                                <h2>Form</h2>
                            </div>
                        </div>
                        <div className="row justify-content-center">
                            <div className="column-auto">
                                <h3 className="form-title">{form.name} - {Mapping[form.formTypeId]}</h3>
                            </div>
                        </div>
                        <div className="form-main">
                            {newFieldsDisabled && <div className="text-bold">Only one field is allowed on an Outputs and Outcomes Form</div>}
                            <Droppable droppableId={`/fields`} isDropDisabled={!form.draft}>
                                {droppableProvided =>
                                (<div className={getclassNames('fields-container', !fields || fields.length === 0 ? 'empty' : '')} ref={droppableProvided.innerRef}>
                                    {(!fields || fields.length === 0) && <div className="draggable-area-directions">Drag a field over to begin building your form</div>}
                                    {fields && fields.length > 0 && (
                                        fields.map((f, i) => {
                                            if (f.adding && f.addingId) {
                                                const DisplayField = DisplayFieldMapping[f.fieldTypeId];
                                                const dummyField = {
                                                    id: '',
                                                    label: 'Unnamed Field',
                                                    name: '',
                                                    order: 0,
                                                    formId: '',
                                                    fieldTypeId: f.fieldTypeId,
                                                    options: [],
                                                    required: false
                                                };
                                                return (<LoadingPlaceholder id={f.addingId} key={f.addingId} className={getPlaceHolderHeight(f.fieldTypeId)}>
                                                    <DisplayField hideButtons={true}
                                                        disabled={true}
                                                        showAdvanced={false}
                                                        preventEdit={true}
                                                        field={dummyField}
                                                        onDelete={() => { }}
                                                        onEdit={() => { }} />
                                                </LoadingPlaceholder>)
                                            }

                                            const additionalClasses: string[] = [];
                                            if (recentlyAddedField.includes(f.id)) {
                                                additionalClasses.push("highlight");
                                            } else if (form.formTypeId === PUBLIC_INTAKE && !isTemplateField(f)) {
                                                additionalClasses.push("highlight");
                                                additionalClasses.push("softer");
                                            }

                                            const field = getField(f);
                                            if (!field) {
                                                return null;
                                            }
                                            return (<React.Fragment key={f.id}>
                                                <Draggable draggableId={`/fields/${f.id}`} index={i} isDragDisabled={!form.draft}>
                                                    {(draggableProvided) => (<div className={getclassNames("field-container", ...additionalClasses)}
                                                        ref={draggableProvided.innerRef}
                                                        {...draggableProvided.draggableProps}
                                                        {...draggableProvided.dragHandleProps}>
                                                        <div className="row align-items-center">
                                                            <div className="column">
                                                                {field}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    )}
                                                </Draggable>

                                            </React.Fragment>);
                                        })
                                    )}
                                    {droppableProvided.placeholder}
                                </div>)}
                            </Droppable>
                        </div>
                    </div>
                </div>
            </DragDropContext>
            <Modal id="edit-modal" visible={editingField !== undefined} className="small" title="Edit Field" onClose={() => setEditingField(undefined)}>
                {editingField &&
                    <>
                        {modalError && <ErrorMessages messages={[modalError]} />}
                        {getEditForm(editingField)}
                    </>
                }
            </Modal>
            <Modal id="delete-modal" visible={deletingField !== undefined} className="small" title="Delete Field" onClose={() => setDeletingField(undefined)}>
                {deletingField &&
                    <>
                        {modalError && <ErrorMessages messages={[modalError]} />}
                        <div className="row">
                            <div className="column">
                                Are you sure you want to delete this field?
                                <br />
                                "{deletingField.label}"
                            </div>
                        </div>
                        <div className="row margin-top-5">
                            <div className="column">
                                <div className="row justify-content-between">
                                    <div className="column-medium-auto">
                                        <Button id="cancel-delete" text="Cancel" className="secondary" disabled={deleting} onClick={() => setDeletingField(undefined)} />
                                    </div>
                                    <div className="column-medium-auto">
                                        <Button id="confirm-delete" text="Delete" className="warning" disabled={deleting} loading={deleting} onClick={handleOnDelete} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                }
            </Modal>
            <Modal id="add-all-profile-fields" visible={showAddAllProfileFields} className="small" title="Confirm Adding Profile Fields" onClose={handleHideAddAllProfileFields}>
                <>
                    {modalError && <ErrorMessages messages={[modalError]} />}
                    <ApiErrorMessages error={apiError} />
                    <div className="row">
                        <div className="column">
                            Select all profile fields you want to add
                        </div>
                    </div>
                    <div className="row margin-vertical-3">
                        <div className="column">
                            <CheckboxGroup id="profile-fields-to-add"
                                options={mainProfileFields.filter(pf => !fields.some(f => f.name === pf.name)).map(pf => {
                                    return {
                                        label: pf.label,
                                        value: pf.name
                                    }
                                })}
                                checkedOptions={selectedProfileFields}
                                label="Profile Fields"
                                onChange={(v) => setSelectedProfileFields(v)} />
                        </div>
                    </div>
                    <div className="row justify-content-between">
                        <div className="column-medium-auto">
                            <Button id="cancel-add"
                                text="Cancel"
                                className="secondary"
                                disabled={addingAllProfileFields}
                                onClick={handleHideAddAllProfileFields} />
                        </div>
                        <div className="column-medium-auto">
                            <div className="row align-items-center">
                                <div className="column-auto">
                                    <Checkbox id="align-fields-to-profile-additional-info"
                                        onChange={setAlignToProfile}
                                        checked={alignToProfile}
                                        containerClassName="margin-top-0 unboxed"
                                        label="Align Fields to Profile" />
                                </div>
                                <div className="column-auto">
                                    <Button id="confirm-add"
                                        text="Add Selected Fields"
                                        disabled={addingAllProfileFields || selectedProfileFields.length === 0}
                                        loading={addingAllProfileFields}
                                        onClick={executeAddAllProfileFields} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            </Modal>
            <Modal id="add-all-additional-info-fields" visible={showAddAllAdditionalInfoFields} className="small" title="Confirm Adding Additional Info Fields" onClose={handleHideAddAllAdditionalInfoFields}>
                <>
                    {modalError && <ErrorMessages messages={[modalError]} />}
                    <ApiErrorMessages error={apiError} />
                    <div className="row">
                        <div className="column">
                            Select all additional info fields you want to add
                        </div>
                    </div>
                    <div className="row margin-vertical-3">
                        <div className="column">
                            <CheckboxGroup id="profile-fields-to-add"
                                options={additionalInfoFields.filter(pf => !fields.some(f => f.name === pf.name)).map(pf => {
                                    return {
                                        label: pf.label,
                                        value: pf.name
                                    }
                                })}
                                checkedOptions={selectedAdditionalInfoFields}
                                label="Additional Info Fields"
                                onChange={(v) => setSelectedAdditionalInfoFields(v)} />
                        </div>
                    </div>
                    <div className="row justify-content-between">
                        <div className="column-medium-auto">
                            <Button id="cancel-add"
                                text="Cancel"
                                className="secondary"
                                disabled={addingAllAdditionalInfoFields}
                                onClick={handleHideAddAllAdditionalInfoFields} />
                        </div>
                        <div className="column-medium-auto">
                            <div className="row align-items-center">
                                <div className="column-auto">
                                    <Checkbox id="align-fields-to-profile-additional-info"
                                        onChange={setAlignToProfile}
                                        checked={alignToProfile}
                                        containerClassName="margin-top-0 unboxed"
                                        label="Align Fields to Profile" />
                                </div>
                                <div className="column-auto">
                                    <Button id="confirm-add"
                                        text="Add Selected Fields"
                                        disabled={addingAllAdditionalInfoFields || selectedAdditionalInfoFields.length === 0}
                                        loading={addingAllAdditionalInfoFields}
                                        onClick={executeAddAllAdditionalInfoFields} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            </Modal>
            <Modal id="align-fields-to-profile" visible={confirmAlignFieldsToProfile} className="small" title="Confirm Aligning Fields to Profile" onClose={handleCancelAlignFieldsToProfile}>
                <>
                    {modalError && <ErrorMessages messages={[modalError]} />}
                    <ApiErrorMessages error={apiError} />
                    <div className="row margin-vertical-5">
                        <div className="column">
                            Are you sure you want to reorder all fields in this form to match the profile?
                        </div>
                    </div>
                    <div className="row justify-content-between">
                        <div className="column-medium-auto">
                            <Button id="cancel-add"
                                text="Cancel"
                                className="secondary"
                                disabled={aligningToFieldsToProfile}
                                onClick={handleCancelAlignFieldsToProfile} />
                        </div>
                        <div className="column-medium-auto">
                            <Button id="confirm-add"
                                text="Yes, Reorder Fields"
                                disabled={aligningToFieldsToProfile}
                                loading={aligningToFieldsToProfile}
                                onClick={handleAlignFieldsToProfile} />
                        </div>
                    </div>
                </>
            </Modal>
        </div>
    </section>)
};

export default FormV2;
